<template>
  <form
    :id="`searchForm${blok._uid}`"
    role="search"
    @submit.prevent="searchQuery.filter = searchTerm"
  >
    <div
      v-if="blok.showSearchBar"
      class="flex"
      :class="{
        'gap-x-1': !blok.includeLocationInSearch,
        'flex-wrap md:flex-nowrap': blok.includeLocationInSearch,
      }"
    >
      <input
        v-model.trim="searchTerm"
        class="w-full bg-white shadow-xs placeholder:text-gray-700 focus:outline-1 sm:text-sm md:w-80"
        :class="
          blok.includeLocationInSearch
            ? 'embedded rounded-t-md md:rounded-l-md md:rounded-r-none'
            : 'rounded-md'
        "
        type="search"
        name="q"
        :placeholder="searchPlaceholder"
        :aria-label="searchAriaLabel"
        @input="
          (event) => {
            event.target.value === '' ? (searchQuery.filter = searchTerm) : ''
          }
        "
      />

      <template v-if="blok.includeLocationInSearch">
        <Combobox v-model="selectedLocation" nullable>
          <div class="location-container relative">
            <ComboboxInput
              class="embedded w-full rounded-bl-md bg-white shadow-xs placeholder:text-gray-700 focus:outline-1 sm:text-sm md:rounded-none md:border-r-0 md:border-l-0 lg:w-[19.5rem]"
              :display-value="(location) => locationLabel(location)"
              placeholder="Locatie"
              @change="locationQuery = $event.target.value"
            />
            <ComboboxButton
              class="absolute inset-y-0 right-0 flex items-center pr-2"
            >
              <ChevronUpDownIcon
                class="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </ComboboxButton>
            <TransitionRoot
              leave="transition ease-in duration-100"
              leave-from="opacity-100"
              leave-to="opacity-0"
              @after-leave="locationQuery = ''"
            >
              <ComboboxOptions
                v-show="locationQuery !== ''"
                class="absolute z-1 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm"
              >
                <div
                  v-if="filteredLocations.length === 0 && locationQuery !== ''"
                  class="relative cursor-default px-4 py-2 text-gray-700 select-none"
                >
                  Er zijn geen locaties gevonden.
                </div>

                <ComboboxOption
                  v-for="location in filteredLocations"
                  :key="location.place_id"
                  v-slot="{ selected, active }"
                  as="template"
                  :value="location"
                >
                  <li
                    class="relative cursor-default py-2 pr-4 pl-10 select-none"
                    :class="active ? `active` : 'text-gray-900'"
                  >
                    <span
                      class="block truncate"
                      :class="{
                        'font-medium': selected,
                        'font-normal': !selected,
                      }"
                    >
                      {{ locationLabel(location) }}
                    </span>
                    <span
                      v-if="selected"
                      class="absolute inset-y-0 left-0 flex items-center pl-3"
                      :class="{
                        'text-white': active,
                        'text-teal-600': !active,
                      }"
                    >
                      <CheckIcon class="h-5 w-5" aria-hidden="true" />
                    </span>
                  </li>
                </ComboboxOption>
              </ComboboxOptions>
            </TransitionRoot>
          </div>
        </Combobox>

        <div class="flex">
          <label :for="`radius${blok._uid}`" class="sr-only">Locatie</label>
          <select
            :id="`radius${blok._uid}`"
            v-model="searchQuery.geoLocation.distance"
            name="radius"
            class="embedded block w-auto bg-white shadow-xs focus:outline-1 sm:text-sm md:mr-2 md:rounded-r-md"
          >
            <option value="1">+1km</option>
            <option value="5">+5km</option>
            <option value="10" selected="">+10km</option>
            <option value="20">+20km</option>
            <option value="30">+30km</option>
            <option value="50">+50km</option>
          </select>
        </div>
      </template>

      <button
        type="submit"
        class="btn-base btn-style-primary flex grow justify-center py-2 sm:text-sm md:w-24 md:grow-0 md:px-4"
        :class="[
          `btn-style-primary-color-${blok.searchColor || 'primary'}`,
          blok.includeLocationInSearch
            ? 'rounded-none rounded-br-md px-2 md:rounded-md'
            : 'rounded-md px-4',
        ]"
        aria-label="zoeken"
      >
        <MagnifyingGlassIcon class="h-5 w-5" />
      </button>
    </div>

    <fieldset
      v-if="blok.showSearchFilters"
      class="mt-4 grid grid-cols-1 gap-2 md:grid-cols-5"
      :class="{ 'mt-4': blok.showSearchBar }"
    >
      <legend class="sr-only">Filters</legend>
      <VacancyFilter
        v-model="searchQuery.education"
        label="Opleiding"
        :tags="filters.education"
        :highlight-color="searchColor"
      />

      <VacancyFilter
        v-model="searchQuery.discipline"
        label="Vakgebied"
        :tags="filters.discipline"
        :highlight-color="searchColor"
      />

      <VacancyFilter
        v-model="searchQuery.workCategory"
        label="Functiecategorie"
        :tags="filters.workCategory"
        :highlight-color="searchColor"
      />

      <VacancyFilter
        v-model="searchQuery.workExperience"
        label="Werkervaring"
        :tags="filters.workExperience"
        :highlight-color="searchColor"
      />

      <VacancyFilter
        v-model="searchQuery.employmentType"
        label="Dienstverband"
        :tags="filters.employmentType"
        :highlight-color="searchColor"
      />

      <VacancyFilter
        v-model="searchQuery.region"
        label="Regio"
        :tags="filters.region"
        :highlight-color="searchColor"
      />
    </fieldset>

    <div
      v-if="blok.showSearchFilters && searchTags?.length"
      class="mt-2 flex flex-wrap gap-2"
    >
      <button
        v-for="tag in searchTags"
        :key="tag.tag"
        type="button"
        class="tag flex items-center rounded-md px-2 py-0.5 text-xs focus:outline-2"
        @click="removeSearchTag(tag.tag, tag.group)"
      >
        <span class="sr-only">verwijderen</span>
        <span class="-mt-0.1">{{ tag.label }}</span>
        <XMarkIcon class="h-4 w-4" />
      </button>
    </div>
  </form>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import {
  CheckIcon,
  ChevronUpDownIcon,
  MagnifyingGlassIcon,
} from '@heroicons/vue/20/solid'
import { XMarkIcon } from '@heroicons/vue/16/solid'
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  TransitionRoot,
} from '@headlessui/vue'
import VacancyFilter from './VacancyFilter.vue'
import { useColorPalette } from '../composables/colorPalette'
import { usePageContext } from '../renderer/usePageContext'
import { useHead } from '@unhead/vue'
import { watchDebounced } from '@vueuse/core'

const props = defineProps({
  blok: {
    type: Object,
    default: () => {},
  },
  filters: {
    type: Object,
    default: () => {},
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
  searchAriaLabel: {
    type: String,
    default: '',
  },
})

const searchQuery = defineModel({ type: Object })
const { colorCustomClass, contrastingGrey } = useColorPalette()

const searchTerm = ref(searchQuery.value.filter)
const selectedLocation = ref(null)
const locationQuery = ref('')
const filteredLocations = ref([])

if (searchQuery.value.geoLocation?.latitude) {
  selectedLocation.value = {
    display_place: searchQuery.value.geoLocation.label,
    lat: searchQuery.value.geoLocation.latitude,
    lon: searchQuery.value.geoLocation.longitude,
  }
}

const searchColor = computed(() => {
  return props.blok.searchColor || 'default'
})
const searchTags = computed(() => {
  return [
    'education',
    'discipline',
    'workCategory',
    'workExperience',
    'employmentType',
    'region',
  ].reduce((result, group) => {
    searchQuery.value[group].forEach((tag) => {
      if (props.filters[group]) {
        const tagFilter = props.filters[group].find(
          (filter) => filter.key === tag || filter.label === tag,
        )
        if (tagFilter) {
          result.push({
            group,
            tag,
            label: tagFilter.label,
          })
        }
      }
    })
    return result
  }, [])
})

function removeSearchTag(tag, group) {
  const tagIndex = searchQuery.value[group].findIndex((item) => item === tag)
  if (tagIndex >= 0) {
    searchQuery.value[group].splice(tagIndex, 1)
  }
}

function locationLabel(location) {
  return `${location?.display_place || ''}${location?.type === 'state' ? ' (Provincie)' : ''}`
}

watch(selectedLocation, (newValue) => {
  searchQuery.value.geoLocation.label = locationLabel(newValue)
  searchQuery.value.geoLocation.latitude = newValue?.lat
  searchQuery.value.geoLocation.longitude = newValue?.lon
})

watchDebounced(
  locationQuery,
  async () => {
    filteredLocations.value = []
    if (locationQuery.value) {
      const response = await fetch(
        `https://api.locationiq.com/v1/autocomplete?q=${locationQuery.value}&key=${import.meta.env.VITE_LOCATION_IQ_TOKEN}&&countrycodes=nl&accept-language=nl&tag=place:city,place:town,place:borough,place:municipality,place:village,place:hamlet,place:state`,
      )
      if (!response.ok) {
        return
      }
      const data = await response.json()
      if (data && data.length) {
        filteredLocations.value = data
      }
    }
  },
  { debounce: 500, maxWait: 5000 },
)

const pageContext = usePageContext()
useHead(() => {
  // We need to set CSS vars inside <head><style> instead of using v-bind() directly inside <style scoped>
  // because v-bind generates style="..." and that's not allowed with CSP enabled.
  const inputBorderColor = contrastingGrey(props.blok.bgVariant)
  const style = [
    {
      key: `${props.blok._uid}-search`,
      nonce: pageContext.nonce,
      innerHTML: `
          #searchForm${props.blok._uid} {
            --btn-color: var(--${colorCustomClass(searchColor.value, null, '')});
            --btn-text: var(--${colorCustomClass(null, searchColor.value, '')});
          }

          #searchForm${props.blok._uid} input,
          #searchForm${props.blok._uid} select,
          #searchForm${props.blok._uid} .listbox-button {
            border-color: ${inputBorderColor};
          }
      `,
    },
  ]

  return {
    style,
  }
})
</script>

<style scoped>
input:focus,
select:focus {
  border-color: var(--btn-color);
  outline-color: var(--btn-color);
}

button.tag {
  background-color: color-mix(in oklab, var(--btn-color), white 85%);
}

button.tag:focus {
  outline-color: var(--btn-color);
}

.location-container {
  width: calc(100% - 9.3rem);
}

@media (min-width: 768px) {
  .location-container {
    width: auto;
  }
}
</style>
